import React from 'react'
import { createStore } from 'zustand'
import { ActionCardModel, FlowData } from '@playbooks/action-cards-utils'
import { ActionCard, Question, Section } from '@playbooks/core/src/interfaces/ActionCard'

import actionUpdate, { ActionUpdate } from './actions/actionUpdate'
import actionSetActionCards, { processActionCardsData, SetActionCards } from './actions/actionSetActionCards'
import { ActionCardsCallbacks, ActionCardsConfig, ActionCardsSettings, SlidesData } from '../types'
import { ActionCardsElement } from '../hooks/useActionCardsApiRegister'
import { Scope } from '../utils/makeSeparatorScopes'
import handleActionCards from '../utils/handleActionCards'

export type ActionCardsProps = ActionCardsCallbacks & {
  value: ActionCard
  settings?: ActionCardsSettings
  // ActionCardsElement ref is needed to use API from the custom element
  ref?: React.RefObject<ActionCardsElement | null>
  config: ActionCardsConfig
}

type ActionCardsState = Omit<ActionCardsProps, 'settings' | 'ref' | 'config'> & {
  data: ActionCard
  slides: SlidesData
  actionCardsModel: ActionCardModel
  questionsWithCode: Question[]
  availableTags: Record<number, string>
  flowData: FlowData
  scopes: Scope[]
  visiblePinnedSections: Section[]

  isScrollButtonVisible: boolean
  update: ActionUpdate
  setActionCards: SetActionCards
  getActualState: () => Store
  setScrollButtonVisible: (visible: boolean) => void
}

export type Store = Omit<ActionCardsState, 'value'>

export const createActionCardsStore = (
  initProps: ActionCardsProps,
  props: {
    prevActionCardsDataRef: React.RefObject<ActionCard | null>
  },
) => {
  const { availableTags, data, flowData, questionsWithCode } = processActionCardsData(initProps.value)

  handleActionCards(data)

  const actionCardsModel = new ActionCardModel(data || null!)

  // We need to get a new objet to prevent direct mutation
  props.prevActionCardsDataRef.current = JSON.parse(JSON.stringify(data)) as ActionCard

  return createStore<Store>()(
    (set, get): Store => ({
      ...initProps,
      scopes: [],
      slides: {},
      data,
      actionCardsModel,
      availableTags,
      questionsWithCode,
      flowData,
      visiblePinnedSections: [],
      isScrollButtonVisible: false,
      setActionCards: actionSetActionCards(set),
      update: actionUpdate(set, get, props),
      getActualState: get,

      setScrollButtonVisible: visible => set({ isScrollButtonVisible: visible }),
    }),
  )
}

export type Get = () => Store
export type Set = (state: Store | ((state: Store) => Store)) => void
